import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { useFormDebug } from './FormDebugProvider';
import FormProviderRenderer from './FormProviderRenderer';

const FormDefaultValuesContext = createContext({});

/**
 * FormDefaultValuesProvider is a React context provider that supplies default form values to its children.
 *
 * Props:
 * - children: React nodes to render inside the provider.
 * - defaultValues: Object containing default values for the form. If null, fetchDefaultValues will be used.
 * - fetchDefaultValues: Optional async function to fetch default values if defaultValues is not provided.
 *
 * Behavior:
 * - If defaultValues is provided, it is used directly.
 * - If not, fetchDefaultValues is called to retrieve the values.
 * - Handles loading and error states, displaying FormLoading and FormErrorsArray components as needed.
 * - Provides the resolved default values via FormDefaultValuesContext.
 */
export function FormDefaultValuesProvider({
  children,
  defaultValues = null,
  fetchDefaultValues = null,
}) {
  const debug = useFormDebug();
  const [formDefaultValues, setFormDefaultValues] = useState(null);
  const [formWarnings, setFormWarning] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  const initialize = async () => {
    setLoading(true);
    try {
      if (defaultValues === null) {
        if (
          fetchDefaultValues === null ||
          typeof fetchDefaultValues !== 'function'
        ) {
          setErrors([
            {
              message:
                'Neither form defaultValues variable nor Form DefaultValues fetcher function is defined',
            },
          ]);
        } else {
          const formDefaultValuesFound = await fetchDefaultValues();
          debug.add('FormDefaultValuesProvider', formDefaultValuesFound);
          if (formDefaultValuesFound?.data) {
            setFormDefaultValues(formDefaultValuesFound?.data);
          } else if (formDefaultValuesFound?.errors) {
            setErrors(formDefaultValuesFound?.errors);
          } else {
            setErrors([
              {
                message: 'Could not fetch form default values',
              },
            ]);
          }
          if (formDefaultValuesFound?.warnings) {
            setFormWarning(formDefaultValuesFound?.warnings);
          }
        }
      } else {
        setFormDefaultValues(defaultValues);
      }
    } catch (err) {
      setErrors([{ message: err?.message }]);
    }

    setLoading(false);
  };

  useEffect(() => {
    debug.addStep('FormDefaultValues');
    initialize();
  }, [defaultValues, fetchDefaultValues]);

  const value = useMemo(
    () => ({ formDefaultValues, formWarnings }),
    [formDefaultValues, formWarnings],
  );

  return (
    <FormDefaultValuesContext.Provider value={value}>
      <FormProviderRenderer
        message="Loading form values"
        loading={loading}
        errors={errors}>
        {children}
      </FormProviderRenderer>
    </FormDefaultValuesContext.Provider>
  );
}

export const useFormDefaultValues = () => {
  return useContext(FormDefaultValuesContext);
};
